import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy} from '@angular/core';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {ICellRendererParams} from 'ag-grid-community';
import {DataAdditionalIcons} from '../../../../shared-utilities/models-old/ngp-report-grid/data-additional-icons';
import {IconColourEnum} from '../../../shared-icons/models/icons-colour.enum';
import {Store} from '@ngrx/store';
import {keyToTitle, StockItem} from '../../../../shared-utilities/models-old/datastructures';
import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {selectCurrentNavigationPage} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {selectIsStockItemsUpdating} from '../../../../features/stock-manager/store/stock-manager.selectors';
import {getStockManagerEditableChangedFieldDescriptions, stockManagerHasEditableFieldChanges} from '../../../../features/stock-manager/utils/stock-manager-update.utils';
import {
  stockItemHasMultiplePriceChanges,
} from '../../../../shared/shared-utils/price/multiple-price-change.utils';
import {openMultiplePriceChangePreview} from '../../../../features/stock-manager/store/stock-manager.actions';

/**
 * CellRendererIconsComponent renders custom HTML within an AG Grid Cell
 * The component displays icons according to additional data provided in the
 * column definitions, and whether it is set to be displayed within the object.
 */
@Component({
    selector: 'app-cell-renderer-icons',
    templateUrl: './cell-renderer-icons.component.html',
    styleUrls: ['./cell-renderer-icons.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CellRendererIconsComponent implements ICellRendererAngularComp, OnDestroy {

  readonly icons: typeof IconsEnum = IconsEnum;
  iqNullData = new Date(1899, 11, 30, 1, 30).getTime();
  params: ICellRendererParams;
  additionalData: DataAdditionalIcons
    & {
    costMiss: boolean;
    created: boolean;
    isEdited: boolean;
    error: boolean;
  };
  isStockManager: boolean;
  isStockItemsUpdating: boolean;
  protected readonly iconColourEnum = IconColourEnum;

  private destroy$ = new Subject<boolean>();

  constructor(
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  agInit(
    params: ICellRendererParams &
      { dataAdditional: DataAdditionalIcons },
  ): void {
    this.assignComponentProperties(params);
    this.params = params;
    this.params.data = params.data;
    this.store.select(selectCurrentNavigationPage).pipe(takeUntil(this.destroy$)).subscribe((currentpage: string) => {
      this.isStockManager = currentpage === 'stock-manager';
      this.cdr.markForCheck();
    });
    this.store.select(selectIsStockItemsUpdating).pipe(takeUntil(this.destroy$)).subscribe((isUpdating: boolean) => {
      this.isStockItemsUpdating = isUpdating;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }


  refresh(params: ICellRendererParams &
    { dataAdditional: DataAdditionalIcons }): boolean {
    this.params = params;
    this.assignComponentProperties(params);
    this.cdr.markForCheck();
    return true;
  }

  /**
   * True when the store's multiple price change rules derived new values for sell prices 2-10.
   */
  hasMultiplePriceChanges(): boolean {
    return stockItemHasMultiplePriceChanges(this.params?.data as Record<string, unknown>);
  }

  onMultiplePriceChangeClick(): void {
    const code = String(this.params?.data?.code ?? '');
    if (!code) {
      return;
    }
    this.store.dispatch(openMultiplePriceChangePreview({code}));
  }

  isNewItem(): boolean {
    const lastDate2 = new Date(this.params.data.suppUsedLastDate2 as string).getTime();
    return +this.params.data.suppUsedLastPrice2 === 0 || lastDate2 === this.iqNullData;
  }

  showEditedItems(data: StockItem | NGPReport): string {
    if (!data.originalValue) {
      return '';
    }

    if (this.isStockManager) {
      return getStockManagerEditableChangedFieldDescriptions(data as StockItem).join('\n');
    }

    const fieldsToIgnore = ['originalValue', 'isEdited', 'isError', 'new', 'isSelected'];
    const changedFields: string[] = [];

    function isEqualFlexible<AType>(a: AType, b: AType): boolean {
      if (a === b) {
        return true;
      }

      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
          return false;
        }

        for (let i = 0; i < a.length; i++) {
          if (!isEqualFlexible(a[i], b[i])) {
            return false;
          }
        }
        return true;
      }

      if (a && b && typeof a === 'object' && typeof b === 'object') {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) {
          return false;
        }
        for (const key of aKeys) {
          if (!(key in (b))) {
            return false;
          }
          if (!isEqualFlexible((a)[key], (b)[key])) {
            return false;
          }
        }
        return true;
      }

      return a == b;
    }

    for (const field in data.originalValue) {
      if (!data.originalValue[field] || !('value' in data.originalValue[field])) {
        continue;
      }

      const currentValue = data[field];
      const originalVal = data.originalValue[field].value;

      if (
        !isEqualFlexible(currentValue, originalVal) &&
        !fieldsToIgnore.includes(field) &&
        keyToTitle[field]
      ) {
        changedFields.push(
          `${keyToTitle[field]} changed from ${originalVal} to ${currentValue}`,
        );
      }
    }
    return changedFields.join('\n');
  }


  /**
   * Assign the incoming data to component scope properties.
   *
   * @param params The parameters that are sent through from AG Grid, with dataAdditionalIcon properties
   */
  private assignComponentProperties(
    params: ICellRendererParams &
      { dataAdditional: DataAdditionalIcons }): void {
    const isStockManagerGrid = Boolean(
      (params.context as { isStockManager?: boolean } | undefined)?.isStockManager,
    );
    const rowData = params?.data as StockItem | undefined;
    const isRowEdited = isStockManagerGrid && rowData
      ? stockManagerHasEditableFieldChanges(rowData)
      : Boolean(params?.data?.isEdited);

    this.additionalData = params?.dataAdditional ? {
      ...params.dataAdditional,
      costMiss: params?.data?.costMiss || false,
      created: params?.data?.created || false,
      isEdited: isRowEdited,
      error: params?.data?.error || false,
    } : null;
  }

}
